/**
 * Where to go, along the bottom, on a phone.
 *
 * The four destinations used to live in a drawer behind the hamburger, which
 * meant the app opened showing no way to anywhere — the complaint that started
 * this. Putting one of them in the bar helped that one and left the other
 * three exactly where they were, and a bar is the wrong place anyway: the
 * top-left corner of a modern phone is the furthest point from a thumb.
 *
 * So they all sit at the bottom, labelled, always visible. Three to five is
 * what a tab bar is for — below three it is not worth the height, above five
 * the targets get too narrow to hit.
 *
 * Today came first as the fifth: it is where the app opens, what to do in
 * the garden this morning.
 *
 * Every icon keeps its word underneath. A leaf on its own does not say
 * "plants" as opposed to anything else in a gardening app, and the small set
 * of glyphs that reads unlabelled — search, close, back, menu — contains none
 * of these five.
 *
 * What to plant took Photos' fourth place (D67; Photo sort moved into the account menu).
 *
 * Weather had the fourth slot until Seasons took it: the garden by plant,
 * by place, by picture and by time. The weather is in the header instead,
 * on every tab (see WeatherPill), and still in the block at the top of
 * Plants.
 *
 * The current tab is a filled square in the theme's "where you are" colour
 * (active), and stays lit on its sub-pages (a plant page keeps Plants lit).
 *
 * Phones only. Above md the header carries the same five across the top,
 * where a cursor reaches them fine and the height is not precious.
 *
 * Its words follow the language as soon as the preferences arrive: it reads
 * the user config for its updates only, as the Header does. Without that it
 * drew once in English on a fresh load and stayed English under a Spanish
 * header.
 *
 * Used by: Layout only.
 *
 * @module TabBar
 */

import { Link, useLocation } from 'react-router-dom';
import { CalendarDays, MapPin, Shovel, Sprout, Sunrise } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useUserConfig } from '@/shared/context/userConfigStore';
import { cn } from '@/lib/utils';
import { isDestinationActive } from './isDestinationActive';

/** The same five the header lists above md, in the order they get used. */
const TABS = [
  { to: '/today', key: 'nav.today', Icon: Sunrise },
  { to: '/plants', key: 'nav.plants', Icon: Sprout },
  { to: '/beds', key: 'nav.locations', Icon: MapPin },
  { to: '/what-to-plant', key: 'nav.whatToPlant', Icon: Shovel },
  { to: '/season', key: 'nav.seasonsTab', Icon: CalendarDays },
] as const;

/**
 * Today · Plants · Beds · What to plant · Seasons along the bottom, on a phone.
 *
 * Not there until the garden has a postcode: Today asks for it (the welcome),
 * and the other tabs have nothing to show without it. Today alone is no bar.
 *
 * @returns The bar, or null until the garden has a postcode.
 */
export function TabBar(): JSX.Element | null {
  const { reactTranslate: t } = useTranslation();
  // Read for its updates only: a language change redraws the labels.
  useUserConfig();
  const { usable } = useGardenPostcode();
  const { pathname } = useLocation();
  if (!usable) return null;

  return (
    <nav
      aria-label={t('nav.primary')}
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t md:hidden',
        'bg-card border-border',
      )}
      /*
       * Zero today: the viewport meta has no `viewport-fit=cover`, so a
       * standalone window already stops short of Android's gesture bar and
       * the inset resolves to nothing. Written anyway so the bar does not
       * end up underneath it the day that meta changes.
       */
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      {TABS.map(({ to, key, Icon }) => {
        const isActive = isDestinationActive(pathname, to);
        return (
          <Link
            key={to}
            to={to}
            aria-current={isActive ? 'page' : undefined}
            className="flex min-h-[3.25rem] items-center justify-center px-0.5 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
          >
            {/* The current tab (and the pages under it) is a filled square in the
                theme's "where you are" colour (not a pill: a pill's round ends cut
                "What to plant" and "Seasons" short); the others stay muted. */}
            <span
              className={cn(
                'flex w-full flex-col items-center justify-center gap-1 rounded px-0.5 py-1.5 transition-colors',
                isActive
                  ? 'bg-active text-active-foreground'
                  : 'text-muted-foreground',
              )}
            >
              <Icon className="h-5 w-5 shrink-0" strokeWidth={isActive ? 2.25 : 1.75} aria-hidden="true" />
              <span className={cn(
                'whitespace-nowrap text-[0.6875rem] leading-none tracking-tight font-body',
                // Same weight when current: bold "What to plant" ran past the square's edges.
              )}>
                {t(key)}
              </span>
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
